import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Package, CheckCircle, AlertCircle, Loader, History, Calendar, RefreshCcw } from 'lucide-react';
import { 
  getPendingDeclarations, 
  declareAsset,
  getPendingAnnualDeclarations,
  submitAnnualDeclaration,
  getDeclarationHistory
} from '../../../models/assetsModel';

export default function AssetDeclarationPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('initial'); // 'initial', 'annual', 'history'
  const [declarations, setDeclarations] = useState([]);
  const [annualDeclarations, setAnnualDeclarations] = useState([]);
  const [declarationHistory, setDeclarationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [confirmations, setConfirmations] = useState({});
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all types of declarations in parallel
      const [initialData, annualData, historyData] = await Promise.all([
        getPendingDeclarations().catch(e => ({ declarations: [] })),
        getPendingAnnualDeclarations().catch(e => ({ declarations: [] })),
        getDeclarationHistory().catch(e => ({ history: [] }))
      ]);
      
      setDeclarations(initialData.declarations || []);
      setAnnualDeclarations(annualData.declarations || []);
      setDeclarationHistory(historyData.history || []);
      
      // Auto-switch to annual tab if no initial declarations but annual ones exist
      if ((initialData.declarations || []).length === 0 && (annualData.declarations || []).length > 0) {
        setActiveTab('annual');
      }
    } catch (error) {
      console.error('Error fetching declarations:', error);
      toast.error('Failed to load declarations');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationToggle = (allocationId) => {
    setConfirmations(prev => ({
      ...prev,
      [allocationId]: !prev[allocationId]
    }));
  };

  const handleDeclareAsset = async (allocation, isAnnual = false) => {
    const allocationId = allocation.allocation_id || allocation.id;
    
    if (!confirmations[allocationId]) {
      toast.warning('Please confirm that you have received/possess the asset');
      return;
    }

    setSubmitting(prev => ({ ...prev, [allocationId]: true }));
    
    try {
      const declarationText = isAnnual
        ? `I confirm that I still have possession of ${allocation.asset_name || 'asset'} (${allocation.asset_tag || allocation.asset_serial || 'N/A'}) for the year ${currentYear} as of ${new Date().toLocaleDateString()}`
        : `I confirm receipt of ${allocation.asset_name || 'asset'} (${allocation.asset_tag || allocation.asset_serial || 'N/A'}) on ${new Date().toLocaleDateString()}`;
      
      if (isAnnual) {
        const response = await submitAnnualDeclaration({
          allocation_id: allocationId,
          declaration: declarationText
        });
        setAnnualDeclarations(prev => prev.filter(d => (d.allocation_id || d.id) !== allocationId));
        toast.success(response?.message || 'Asset declaration submitted successfully.');
      } else {
        const response = await declareAsset({
          allocation_id: allocationId,
          declaration: declarationText
        });
        setDeclarations(prev => prev.filter(d => (d.allocation_id || d.id) !== allocationId));
        toast.success(response?.message || 'Asset declaration submitted successfully.');
      }
      
      // Refresh history
      const historyData = await getDeclarationHistory().catch(e => ({ history: [] }));
      setDeclarationHistory(historyData.history || []);
      
      // Trigger notification refresh in navbar
      window.dispatchEvent(new CustomEvent('refreshNotifications'));
    } catch (error) {
      console.error('Error submitting declaration:', error);
      toast.error('Failed to submit declaration');
    } finally {
      setSubmitting(prev => ({ ...prev, [allocationId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Pending HOD</span>;
      case 1:
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Approved</span>;
      case 2:
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Rejected</span>;
      default:
        return null;
    }
  };

  const totalPending = declarations.length + annualDeclarations.length;

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading declarations...</p>
        </div>
      </div>
    );
  }

  const renderDeclarationCard = (allocation, isAnnual = false) => {
    const allocationId = allocation.allocation_id || allocation.id;
    const isSubmitting = submitting[allocationId];
    const isConfirmed = confirmations[allocationId];
    const borderColor = isAnnual ? 'border-blue-500' : 'border-green-500';
    const headerBg = isAnnual ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200';
    const iconColor = isAnnual ? 'text-blue-600' : 'text-green-600';
    const headerTextColor = isAnnual ? 'text-blue-800' : 'text-green-800';
    const buttonBg = isAnnual ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
    const checkboxColor = isAnnual ? 'text-blue-600 focus:ring-blue-500' : 'text-green-600 focus:ring-green-500';
    
    return (
      <div
        key={allocationId}
        className={`bg-white shadow-sm border-2 ${borderColor} rounded-lg overflow-hidden`}
      >
        {/* Asset Header */}
        <div className={`${headerBg} border-b px-6 py-3`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {isAnnual ? (
                <RefreshCcw className={`w-5 h-5 ${iconColor} mr-2`} />
              ) : (
                <Package className={`w-5 h-5 ${iconColor} mr-2`} />
              )}
              <span className={`font-semibold ${headerTextColor} text-sm`}>
                {isAnnual 
                  ? `Annual Declaration ${currentYear}` 
                  : `Allocated on ${allocation.allocate_date || 'N/A'}`}
              </span>
            </div>
            {isAnnual && allocation.last_declaration_year && (
              <span className="text-xs text-gray-500">
                Last declared: {allocation.last_declaration_year}
              </span>
            )}
          </div>
        </div>

        {/* Asset Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Asset Name</label>
              <p className="text-base font-semibold text-gray-900">
                {allocation.asset_name || 'N/A'}
              </p>
            </div>
            
            {allocation.asset_tag && (
              <div>
                <label className="text-sm font-medium text-gray-500">Asset Tag</label>
                <p className="text-base font-semibold text-gray-900">
                  {allocation.asset_tag}
                </p>
              </div>
            )}
            
            {allocation.asset_serial && (
              <div>
                <label className="text-sm font-medium text-gray-500">Serial Number</label>
                <p className="text-base text-gray-900">
                  {allocation.asset_serial}
                </p>
              </div>
            )}
            
            {allocation.category && (
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="text-base text-gray-900">
                  {allocation.category}
                </p>
              </div>
            )}
          </div>

          {/* Additional Info from allocate_data */}
          {allocation.allocate_data && (() => {
            try {
              const allocData = typeof allocation.allocate_data === 'string' 
                ? JSON.parse(allocation.allocate_data) 
                : allocation.allocate_data;
              
              if (allocData && Object.keys(allocData).length > 0) {
                return (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Additional Items:</p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {allocData.carry_bag && <li>• Carry Bag: {allocData.carry_bag}</li>}
                      {allocData.keyboard && <li>• Keyboard included</li>}
                      {allocData.mouse && <li>• Mouse included</li>}
                      {allocData.windows_version && <li>• Windows Version: {allocData.windows_version}</li>}
                      {allocData.office_version && <li>• Office Version: {allocData.office_version}</li>}
                      {allocData.other_hardware && <li>• Other: {allocData.other_hardware}</li>}
                    </ul>
                  </div>
                );
              }
            } catch (e) {
              return null;
            }
          })()}

          {/* Confirmation Checkbox */}
          <div className="flex items-start mb-4">
            <input
              type="checkbox"
              id={`confirm-${allocationId}`}
              checked={isConfirmed || false}
              onChange={() => handleConfirmationToggle(allocationId)}
              disabled={isSubmitting}
              className={`mt-1 h-4 w-4 ${checkboxColor} border-gray-300 rounded disabled:opacity-50`}
            />
            <label
              htmlFor={`confirm-${allocationId}`}
              className="ml-3 text-sm text-gray-700"
            >
              {isAnnual
                ? `I confirm that I still have this asset in my possession and it is in good condition as of ${currentYear}`
                : 'I confirm that I have received this asset and all listed items in good condition'}
            </label>
          </div>

          {/* Submit Button */}
          <button
            onClick={() => handleDeclareAsset(allocation, isAnnual)}
            disabled={isSubmitting || !isConfirmed}
            className={`w-full px-4 py-3 ${buttonBg} text-white text-base font-medium rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center`}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Submit {isAnnual ? 'Annual ' : ''}Declaration
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[70vh] bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Asset Declarations
                </h1>
                <p className="text-gray-600">
                  {totalPending > 0 ? (
                    <>
                      You have <span className="font-semibold text-orange-600">{totalPending}</span> asset{totalPending !== 1 ? 's' : ''} awaiting declaration.
                    </>
                  ) : (
                    'All your asset declarations are up to date.'
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={fetchAllData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCcw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('initial')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'initial'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              } transition-colors`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              New Assets
              {declarations.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                  {declarations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('annual')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'annual'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              } transition-colors`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Annual {currentYear}
              {annualDeclarations.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                  {annualDeclarations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'history'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              } transition-colors`}
            >
              <History className="w-4 h-4 inline mr-2" />
              History
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'initial' && (
          <div className="space-y-4">
            {declarations.length === 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-gray-600">No new assets awaiting initial declaration.</p>
              </div>
            ) : (
              declarations.map(allocation => renderDeclarationCard(allocation, false))
            )}
          </div>
        )}

        {activeTab === 'annual' && (
          <div className="space-y-4">
            {annualDeclarations.length === 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <CheckCircle className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <p className="text-gray-600">All your assets have been declared for {currentYear}.</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Annual Asset Verification:</strong> Please confirm that you still have possession of each asset below. 
                    This is required annually to maintain accurate asset records.
                  </p>
                </div>
                {annualDeclarations.map(allocation => renderDeclarationCard(allocation, true))}
              </>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {declarationHistory.length === 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No declaration history found.</p>
              </div>
            ) : (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Declared</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">HOD</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {declarationHistory.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{record.asset_name}</div>
                          <div className="text-xs text-gray-500">{record.asset_tag || record.asset_serial}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{record.declaration_year}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {record.declared_at ? new Date(record.declared_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(record.hod_status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {record.hod_name || '-'}
                          {record.hod_date && (
                            <div className="text-xs">{new Date(record.hod_date).toLocaleDateString()}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Footer Actions */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
